/**
 * Leads Ops dashboard shapes, as the BFF returns them.
 *
 * Mirrors `lead-ops-report.dto.ts` in server.service.lead, plus the two things
 * the BFF adds: a `user` on every row carrying a userId, and the `teams` fold.
 */
export enum OpsDateBasis {
  CREATED = 'created',
  RESOLVED = 'resolved'
}

export interface OpsFilterModel {
  dateFrom?: string;
  dateTo?: string;
  dateBasis?: OpsDateBasis;
  vertical?: string[];
  status?: string[];
  resolutionType?: string[];
  valueCategory?: string[];
  marketingChannel?: string[];
  source?: string[];
  assignedTo?: string[];
  agentLimit?: number;
}

export interface OpsCountModel {
  key: string;
  count: number;
}

export interface OpsIntegrityModel {
  totalLeads: number;
  statusSum: number;
  statusBalanced: boolean;
  resolvedTotal: number;
  resolutionSum: number;
  resolvedWithoutOutcome: number;
  outcomesOutsideResolved: number;
}

export interface OpsSummaryModel {
  totalLeads: number;
  byStatus: OpsCountModel[];
  byResolution: OpsCountModel[];
  byVertical: OpsCountModel[];
  byChannel: OpsCountModel[];
  dailyTrend: { date: string; total: number; resolved: number }[];
  integrity: OpsIntegrityModel;
}

/** `userIdUser` is the hydrated person; the BFF attaches it per row. */
export interface HydratedRef {
  id: string;
  name?: string;
  email?: string;
  image?: string;
}

export interface OpsAgentModel {
  userId: string;
  userIdUser?: HydratedRef;
  managerId?: string | null;
  total: number;
  new: number;
  pending: number;
  reopened: number;
  notReachable: number;
  duplicate: number;
  resolved: number;
  won: number;
  notInterested: number;
  notQualified: number;
  resolvedRate: number;
  winRate: number;
  slaMet: number;
  slaBreached: number;
}

export interface OpsTeamModel {
  managerId: string | null;
  managerName: string;
  headcount: number;
  total: number;
  resolved: number;
  won: number;
  pending: number;
  notReachable: number;
  resolvedRate: number;
  winRate: number;
}

export interface OpsAgentPerformanceModel {
  agents: OpsAgentModel[];
  teams: OpsTeamModel[];
  agentTotal: number;
  agentLimit: number;
}

export interface OpsPendingPipelineModel {
  total: number;
  byReason: OpsCountModel[];
  aging: OpsCountModel[];
  byChannel: OpsCountModel[];
  byValueTier: OpsCountModel[];
  byVertical: OpsCountModel[];
}

export interface OpsLostAgentModel {
  userId: string;
  userIdUser?: HydratedRef;
  resolved: number;
  lost: number;
  lostRate: number;
}

export interface OpsLostAnalysisModel {
  totals: {
    lost: number;
    resolvedTotal: number;
    lostRate: number;
    notInterested: number;
    notQualified: number;
  };
  byReason: OpsCountModel[];
  byChannel: OpsCountModel[];
  byVertical: OpsCountModel[];
  byValueTier: OpsCountModel[];
  dailyTrend: { date: string; count: number }[];
  byAgent: OpsLostAgentModel[];
  peerMedianRate: number;
  minResolvedThreshold: number;
}

export interface OpsVerticalFunnelModel {
  verticals: {
    vertical: string;
    total: number;
    resolved: number;
    won: number;
    lost: number;
    wonBreakdown: OpsCountModel[];
    conversionRate: number;
  }[];
}

export interface OpsSlaAttemptsModel {
  sla: {
    met: number;
    breached: number;
    awaiting: number;
    atRisk: number;
    unknown: number;
    metRate: number;
  };
  firstResponseBuckets: OpsCountModel[];
  medianFirstResponseMinutes: number | null;
  attempts: {
    exhausted: number;
    unreachable: number;
    untouched: number;
    averageAttempts: number;
  };
  slaByVertical: { vertical: string; met: number; breached: number; metRate: number }[];
}
